import React, { useEffect, useRef, useState } from "react";
import { PRODUCTS } from "../products";
import { fetchUpsells, startCheckout } from "../lib/checkout";
import { registerUpsellHost } from "../lib/upsell";

const INK = "#141414";
const CREAM = "#FAF7EF";
const CREAM_ALT = "#F3EFE2";
const WHITE = "#FFFFFF";
const LIME = "#D9F154";
const BLUE = "#3B5EDF";
const RED = "#E5484D";
const SECONDS = 300;
// Same VSL video plays at the top of every upsell offer landing.
const OFFER_VIDEO = "/upsell/oferta1.mp4";

// Persuasive, on-brand pre-close copy per upsell product (fresh, direct, close tone).
const UPSELL_COPY = {
  toolkit: {
    headline: "Llévate mis plantillas y deja de armar todo desde cero.",
    sub: "El mismo arsenal con el que preparo un fondeo institucional.",
    bullets: [
      "Modelo de cartera y waterfall listos para llenar",
      "Data room de 80 puntos que piden los fondos",
      "Term sheet anotado: qué se negocia y qué no",
      "Checklists de cierre para no perder el deal",
    ],
    pitch:
      "Armar esto desde cero te toma semanas — y un error caro. Aquí lo tienes listo y probado en deals reales. Lo abres, lo llenas, lo mandas.",
  },
  ebook_bundle: {
    headline: "El panorama completo del capital, en 3 ebooks.",
    sub: "PyMEs · Lenders · FinCorp. Todo junto, a mitad de precio.",
    bullets: [
      "Los 3 ebooks en un solo paquete",
      "Del crédito PyME al capital markets",
      "Lectura directa, cero relleno",
      "Empiezas a aplicarlo hoy mismo",
    ],
    pitch:
      "Si vas a estructurar capital en serio, necesitas ver el mapa completo — no una sola pieza. Estos 3 ebooks te lo dan de una.",
  },
  course_pymes: {
    headline: "Consigue capital de trabajo sin morir en factoring.",
    sub: "El curso aplicado para PyMEs que quieren crédito de verdad.",
    bullets: [
      "Cómo conseguir crédito bancario real",
      "Deja de pagar factoring al 40%",
      "Video-lecciones + plantillas aplicadas",
      "Casos reales de PyMEs en LATAM",
    ],
    pitch:
      "La mayoría de las PyMEs pagan de más por capital que ni necesitaban así. Este curso te enseña a estructurarlo bien desde la primera vez.",
  },
  course_lenders: {
    headline: "Estructura tu warehouse y cierra tu primera línea.",
    sub: "El paso a paso para plataformas de crédito.",
    bullets: [
      "Warehouse facility de principio a fin",
      "Loan tape, covenants y advance rate",
      "Cómo piensan de verdad los fondos de deuda",
      "Cierra tu primera línea institucional",
    ],
    pitch:
      "Levantar deuda para prestar es otro juego. Aquí te enseño exactamente cómo se estructura y se negocia — con lo que uso en deals reales.",
  },
  course_fincorp: {
    headline: "Corporate finance y capital markets, por dentro.",
    sub: "M&A, bonos, emisiones e IPO — el nivel que piden los bancos.",
    bullets: [
      "M&A y valuación aplicada",
      "Bonos, emisiones y mercados de capital",
      "El camino real hacia una IPO",
      "Explicado por quien firmó $1.2B+",
    ],
    pitch:
      "El conocimiento que separa a un analista de un managing partner. Aplicado, sin teoría de libro.",
  },
  cert_fincorp: {
    headline: "Conviértelo en una credencial que abre puertas.",
    sub: "Certificación profesional avalada por IADA Anáhuac.",
    bullets: [
      "Certificación en Corporate Finance & Capital Markets",
      "Aval académico de IADA Anáhuac",
      "Diploma verificable para tu LinkedIn y CV",
      "El sello que te toman en serio",
    ],
    pitch:
      "El conocimiento vale, pero la credencial avalada te abre la puerta. Súmala ahora al 50% y sales certificado.",
  },
  ebook_intangibles: {
    headline: "Tus intangibles valen — aprende a levantar deuda contra ellos.",
    sub: "Marca, IP, datos, contratos y cartera recurrente convertidos en colateral.",
    bullets: [
      "Qué intangibles sí acepta un banco o fondo",
      "Cómo valuarlos sin humo ni teoría de libro",
      "De activo a colateral: borrowing base y advance rate",
      "El caso real de una fintech que fondeó su cartera",
    ],
    pitch:
      "La mayoría deja millones sobre la mesa porque nadie les enseñó a valuar lo que ya tienen. Este ebook te da el mapa — y al 50% es una decisión fácil antes de cerrar.",
  },
  course_intangibles: {
    headline: "El método completo para fondear tus intangibles.",
    sub: "Valuación aplicada + estructura de deuda, con plantillas listas.",
    bullets: [
      "Los 6 métodos de valuación con plantilla en Excel",
      "Cartera recurrente / ARR como colateral",
      "IP, marca y patentes: valuación y gravamen",
      "Memo de valuación y data room para el prestamista",
    ],
    pitch:
      "Del intangible al desembolso: el mismo proceso que uso para estructurar líneas contra activos que el banco 'no ve'. Súmalo al 50% y llévate las plantillas.",
  },
};

function parseMoney(str) {
  const n = parseFloat(String(str || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function fmtUsd(n) {
  const whole = Number.isInteger(n);
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}
function halfMxn(str) {
  const n = Math.round(parseMoney(str) / 2);
  return n ? "MXN " + n.toLocaleString("en-US") : null;
}
function clock(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const overlayStyle = {
  position: "fixed", inset: 0, zIndex: 100000,
  background: CREAM, color: INK, overflowY: "auto", WebkitOverflowScrolling: "touch",
};

const btnBig = {
  width: "100%", background: LIME, color: INK, border: `3px solid ${INK}`,
  borderRadius: 18, boxShadow: `7px 7px 0 ${INK}`, padding: "24px 20px",
  fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 19,
  letterSpacing: "0.005em", cursor: "pointer", textTransform: "uppercase", lineHeight: 1.12,
  textAlign: "center",
};

function ClockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Check() {
  return (
    <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 8, background: LIME, border: `2px solid ${INK}`, display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
    </span>
  );
}

function OfferVideo({ src }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(false);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // Browser blocked autoplay with sound — fall back to muted playback and
        // surface a one-tap unmute control.
        v.muted = true;
        setMuted(true);
        v.play().catch(() => {});
      });
    }
  }, [src]);
  const unmute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    setMuted(false);
    v.play().catch(() => {});
  };
  return (
    <div data-testid="upsell-video" style={{ position: "relative", marginBottom: 30 }}>
      <video
        ref={ref}
        src={src}
        autoPlay
        playsInline
        controls
        preload="auto"
        style={{ width: "100%", display: "block", borderRadius: 16, border: `3px solid ${INK}`, boxShadow: `6px 6px 0 ${INK}`, background: "#000" }}
      />
      {muted && (
        <button
          type="button"
          data-testid="upsell-video-unmute"
          onClick={unmute}
          className="up-cta"
          style={{
            position: "absolute", top: 14, right: 14, zIndex: 3,
            display: "inline-flex", alignItems: "center", gap: 8,
            background: LIME, color: INK, border: `2px solid ${INK}`, borderRadius: 999,
            boxShadow: `3px 3px 0 ${INK}`, padding: "9px 15px", cursor: "pointer",
            fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 12,
            letterSpacing: "0.04em", textTransform: "uppercase",
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14" /></svg>
          Activar sonido
        </button>
      )}
    </div>
  );
}

function OfferLanding({ productKey, discountPct, index, total, videoSrc, onAccept, onDecline }) {
  const p = PRODUCTS[productKey];
  const [left, setLeft] = useState(SECONDS);
  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  if (!p) return null;

  const orig = parseMoney(p.usd);
  const now = (orig * (100 - discountPct)) / 100;
  const save = orig - now;
  const mxn = halfMxn(p.mxn);
  const copy = UPSELL_COPY[productKey] || {};
  const headline = copy.headline || `Suma ${p.name} con ${discountPct}% OFF`;
  const sub = copy.sub || p.blurb || "";
  const bullets = copy.bullets || (p.features || []).slice(0, 4);
  const pitch = copy.pitch || p.blurb || "";
  const acceptLabel = `SÍ — AGRÉGALO A MI PEDIDO CON ${discountPct}% OFF`;

  const AcceptBtn = ({ testid }) => (
    <button type="button" data-testid={testid} onClick={onAccept} className="up-cta" style={btnBig}>
      {acceptLabel} →
    </button>
  );

  return (
    <div data-testid="upsell-offer">
      {/* Sticky urgency bar */}
      <div
        style={{
          position: "sticky", top: 0, zIndex: 5, background: RED, color: WHITE,
          borderBottom: `3px solid ${INK}`, padding: "13px 18px",
          display: "flex", gap: 12, alignItems: "center", justifyContent: "center", flexWrap: "wrap",
          fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 13.5, letterSpacing: "0.02em", textAlign: "center",
        }}
      >
        <ClockIcon />
        <span>ESPERA · tu pedido casi está listo — esta oferta termina en</span>
        <span data-testid="upsell-countdown" style={{ fontVariantNumeric: "tabular-nums", fontWeight: 900, fontSize: 16, background: INK, padding: "3px 10px", borderRadius: 8 }}>
          {clock(left)}
        </span>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "40px 22px 64px" }}>
        {videoSrc && <OfferVideo src={videoSrc} />}
        {/* progress + eyebrow */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ background: BLUE, color: WHITE, borderRadius: 999, padding: "5px 12px", fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Oferta {index + 1} de {total}
          </span>
          <span style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.55 }}>
            Una última cosa antes de pagar
          </span>
        </div>

        {/* Huge headline */}
        <h1 style={{ margin: "22px 0 0", fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(32px, 6vw, 52px)", lineHeight: 1.02, letterSpacing: "-0.035em" }}>
          {headline}
        </h1>
        <p style={{ marginTop: 16, fontSize: "clamp(16px, 2.4vw, 20px)", lineHeight: 1.5, opacity: 0.8, fontWeight: 500 }}>
          {sub}
        </p>

        {/* Price anchor */}
        <div style={{ marginTop: 28, background: CREAM_ALT, border: `3px solid ${INK}`, borderRadius: 20, boxShadow: `6px 6px 0 ${INK}`, padding: "24px 22px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ background: RED, color: WHITE, border: `2px solid ${INK}`, borderRadius: 8, padding: "4px 11px", fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 13, letterSpacing: "0.04em" }}>
              {discountPct}% OFF HOY
            </span>
            <span style={{ fontSize: 15, fontWeight: 700, opacity: 0.5 }}>
              Precio normal <span style={{ textDecoration: "line-through" }}>{fmtUsd(orig)}</span>
            </span>
          </div>
          <div style={{ marginTop: 12, display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: "clamp(40px, 9vw, 64px)", letterSpacing: "-0.04em", lineHeight: 1 }}>
              {fmtUsd(now)}
            </span>
            <span style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 16, opacity: 0.65 }}>USD</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 13.5, fontWeight: 800, color: BLUE }}>
            Te ahorras {fmtUsd(save)}{mxn ? ` · ≈ ${mxn}` : ""}
          </div>
        </div>

        {/* Primary CTA #1 */}
        <div style={{ marginTop: 26 }}>
          <AcceptBtn testid="upsell-accept" />
          <div style={{ marginTop: 12, textAlign: "center", fontSize: 12.5, fontWeight: 700, opacity: 0.6, lineHeight: 1.5 }}>
            Se suma a tu compra actual · un solo pago · garantía de 14 días
          </div>
        </div>

        {/* What you get */}
        <div style={{ marginTop: 46 }}>
          <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: "-0.02em" }}>
            Esto es lo que te llevas:
          </div>
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 16 }}>
            {bullets.map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", fontSize: 17, lineHeight: 1.4, fontWeight: 500 }}>
                <Check />
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pitch */}
        {pitch && (
          <p style={{ marginTop: 34, fontSize: 18, lineHeight: 1.6, fontWeight: 500, borderLeft: `4px solid ${LIME}`, paddingLeft: 18 }}>
            {pitch}
          </p>
        )}

        {/* Why now */}
        <div style={{ marginTop: 34, background: INK, color: CREAM, borderRadius: 18, padding: "22px 22px" }}>
          <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 15, letterSpacing: "0.02em", color: LIME, textTransform: "uppercase" }}>
            ¿Por qué ahora?
          </div>
          <p style={{ marginTop: 10, fontSize: 15.5, lineHeight: 1.55, opacity: 0.9 }}>
            Este {discountPct}% solo aparece aquí, mientras cierras tu pedido. Si sales de esta pantalla,
            vuelve a su precio normal de {fmtUsd(orig)}. No se guarda.
          </p>
        </div>

        {/* Primary CTA #2 */}
        <div style={{ marginTop: 30 }}>
          <AcceptBtn testid="upsell-accept-2" />
        </div>

        {/* Tiny decline — all the way at the bottom */}
        <div style={{ marginTop: 30, textAlign: "center" }}>
          <button
            type="button"
            data-testid="upsell-decline"
            onClick={onDecline}
            style={{
              display: "inline-block", background: "none", border: "none", color: INK, opacity: 0.45,
              fontFamily: "'Archivo', sans-serif", fontWeight: 600, fontSize: 12.5,
              textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer", padding: 6,
            }}
          >
            No, gracias — prefiero seguir sin esto
          </button>
        </div>
      </div>

      <style>{`
        .up-cta { transition: transform 0.06s ease, box-shadow 0.06s ease; }
        .up-cta:hover { transform: translate(-2px, -2px); box-shadow: 9px 9px 0 ${INK}; }
        .up-cta:active { transform: translate(4px, 4px); box-shadow: 2px 2px 0 ${INK}; }
      `}</style>
    </div>
  );
}

function FinalizingScreen() {
  return (
    <div data-testid="upsell-finalizing" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
      <div style={{ width: 52, height: 52, borderRadius: "50%", border: `5px solid ${INK}22`, borderTopColor: INK, animation: "uspin 0.8s linear infinite" }} />
      <div style={{ marginTop: 24, fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: "-0.02em" }}>Preparando tu pago seguro…</div>
      <div style={{ marginTop: 10, fontSize: 14.5, opacity: 0.6 }}>Te llevamos a la caja de Stripe en un segundo.</div>
      <style>{`@keyframes uspin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorScreen({ onRetry, onSkip, onClose }) {
  return (
    <div data-testid="upsell-error" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 460, width: "100%", textAlign: "center" }}>
        <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", color: RED }}>
          Algo salió mal
        </div>
        <h2 style={{ marginTop: 10, fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 30, letterSpacing: "-0.03em" }}>No pudimos abrir el pago.</h2>
        <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.5, opacity: 0.8 }}>Puede ser una conexión intermitente. Inténtalo de nuevo.</p>
        <div style={{ marginTop: 26 }}>
          <button type="button" data-testid="upsell-retry" onClick={onRetry} className="up-cta" style={btnBig}>Reintentar →</button>
          <button type="button" data-testid="upsell-skip" onClick={onSkip} style={{ marginTop: 16, background: "none", border: "none", color: INK, opacity: 0.6, fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 13, textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer" }}>Continuar sin extras</button>
          <div><button type="button" data-testid="upsell-error-close" onClick={onClose} style={{ marginTop: 10, background: "none", border: "none", color: INK, opacity: 0.4, fontFamily: "'Archivo', sans-serif", fontWeight: 600, fontSize: 12, textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer" }}>Cancelar</button></div>
        </div>
        <style>{`
          .up-cta { transition: transform 0.06s ease, box-shadow 0.06s ease; }
          .up-cta:hover { transform: translate(-2px, -2px); box-shadow: 9px 9px 0 ${INK}; }
          .up-cta:active { transform: translate(4px, 4px); box-shadow: 2px 2px 0 ${INK}; }
        `}</style>
      </div>
    </div>
  );
}

export default function UpsellFunnelHost() {
  const [flow, setFlow] = useState(null);
  const busyRef = useRef(false);

  const goToForm = (href) => {
    window.location.href = href || "/#apply";
  };

  useEffect(() => {
    registerUpsellHost(async (baseKey, opts = {}) => {
      if (busyRef.current) return;
      busyRef.current = true;
      const isApplication = String(baseKey).startsWith("apply_");
      const formHref = opts.formHref || null;
      let offers = [];
      let discountPct = 50;
      try {
        const data = await fetchUpsells(baseKey);
        offers = (data.offers || []).filter((k) => PRODUCTS[k] && k !== baseKey);
        discountPct = data.discount_pct || 50;
      } catch {
        offers = [];
      }
      if (!offers.length) {
        if (isApplication) {
          // No paid offers to show — send the lead straight to the free application.
          busyRef.current = false;
          goToForm(formHref);
          return;
        }
        setFlow({ baseKey, opts, offers: [], index: 0, accepted: [], discountPct, phase: "finalizing", isApplication, formHref });
        try {
          await startCheckout(baseKey, opts);
        } catch {
          setFlow((f) => (f ? { ...f, phase: "error" } : f));
        }
        return;
      }
      setFlow({ baseKey, opts, offers, index: 0, accepted: [], discountPct, phase: "offer", isApplication, formHref });
    });
    return () => registerUpsellHost(null);
  }, []);

  if (!flow) return null;

  const finalize = async (accepted) => {
    // Free-application funnel with nothing accepted -> route to the application form.
    if (flow.isApplication && (!accepted || accepted.length === 0)) {
      busyRef.current = false;
      setFlow(null);
      goToForm(flow.formHref);
      return;
    }
    setFlow((f) => ({ ...f, phase: "finalizing", accepted }));
    try {
      await startCheckout(flow.baseKey, { ...flow.opts, upsells: accepted });
    } catch {
      setFlow((f) => (f ? { ...f, phase: "error", accepted } : f));
    }
  };
  const advance = (accepted) => {
    if (flow.index + 1 < flow.offers.length) setFlow((f) => ({ ...f, index: f.index + 1, accepted }));
    else finalize(accepted);
  };
  const accept = () => advance([...flow.accepted, flow.offers[flow.index]]);
  const decline = () => advance(flow.accepted);
  const close = () => {
    busyRef.current = false;
    setFlow(null);
  };
  const skip = () => finalize([]);

  return (
    <div data-testid="upsell-funnel" style={overlayStyle}>
      {flow.phase === "offer" && (
        <OfferLanding
          key={flow.index}
          productKey={flow.offers[flow.index]}
          discountPct={flow.discountPct}
          index={flow.index}
          total={flow.offers.length}
          videoSrc={OFFER_VIDEO}
          onAccept={accept}
          onDecline={decline}
        />
      )}
      {flow.phase === "finalizing" && <FinalizingScreen />}
      {flow.phase === "error" && <ErrorScreen onRetry={() => finalize(flow.accepted)} onSkip={skip} onClose={close} />}
    </div>
  );
}
